import { lazy, Suspense, createContext, useContext } from "react";
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";

// Create a context to expose queryClient for cache invalidation on login
const QueryClientContext = createContext<QueryClient | null>(null);
export const useQueryClientContext = () => useContext(QueryClientContext);
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load auth pages - not needed for initial landing page view
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));

// Lazy load less critical pages
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CommunityPage = lazy(() => import("./pages/Community"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ProfileLoading = lazy(() => import("./pages/ProfileLoading"));

// Lazy load dashboard components for code splitting
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout").then(m => ({ default: m.DashboardLayout })));
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const Markets = lazy(() => import("./pages/dashboard/Markets"));
const TradeIdeas = lazy(() => import("./pages/dashboard/TradeIdeas"));
const Community = lazy(() => import("./pages/dashboard/Community"));
const CommunityBoard = lazy(() => import("./pages/dashboard/CommunityBoard"));
const CommunityTopic = lazy(() => import("./pages/dashboard/CommunityTopic"));
const Alerts = lazy(() => import("./pages/dashboard/Alerts"));
const Education = lazy(() => import("./pages/dashboard/Education"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const News = lazy(() => import("./pages/dashboard/News"));
const Calendar = lazy(() => import("./pages/dashboard/Calendar"));
const Courses = lazy(() => import("./pages/dashboard/Courses"));
const CourseDetail = lazy(() => import("./pages/dashboard/CourseDetail"));
const Leaderboard = lazy(() => import("./pages/dashboard/Leaderboard"));
const Administration = lazy(() => import("./pages/dashboard/Administration"));
const AlphaDesk = lazy(() => import("./pages/dashboard/AlphaDesk"));
const AlphaDeskSession = lazy(() => import("./pages/dashboard/AlphaDeskSession"));
const Journal = lazy(() => import("./pages/dashboard/Journal"));
const Support = lazy(() => import("./pages/dashboard/Support"));
const SupportHelp = lazy(() => import("./pages/dashboard/SupportHelp"));
const SupportTickets = lazy(() => import("./pages/dashboard/SupportTickets"));
const SupportTicketNew = lazy(() => import("./pages/dashboard/SupportTicketNew"));
const SupportTicketDetail = lazy(() => import("./pages/dashboard/SupportTicketDetail"));
const CongressionalActivity = lazy(() => import("./pages/dashboard/CongressionalActivity"));

// Import non-lazy components
import { ProtectedRoute } from "./components/dashboard/ProtectedRoute";
import { AdminRoute } from "./components/dashboard/AdminRoute";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes default
      refetchOnWindowFocus: false, // Disable window focus refetch globally
    },
  },
});

// Loading fallback for lazy components
const LazyFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <QueryClientContext.Provider value={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
        <BrowserRouter>
          <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/profile-loading" element={<ProfileLoading />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/community" element={<CommunityPage />} />
              
              {/* Protected Dashboard Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="markets" element={<Markets />} />
                <Route path="ideas" element={<TradeIdeas />} />
                <Route path="community" element={<Community />} />
                <Route path="community/:boardSlug" element={<CommunityBoard />} />
                <Route path="community/:boardSlug/:topicId" element={<CommunityTopic />} />
                <Route path="alerts" element={<Alerts />} />
                <Route path="education" element={<Education />} />
                <Route path="courses" element={<Courses />} />
                <Route path="courses/:courseId" element={<CourseDetail />} />
                <Route path="profile" element={<Profile />} />
                <Route path="news" element={<News />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="leaderboard" element={<Leaderboard />} />
                <Route path="alpha-desk" element={<AlphaDesk />} />
                <Route path="alpha-desk/:sessionId" element={<AlphaDeskSession />} />
                <Route path="journal" element={<Journal />} />
                <Route path="support" element={<Support />} />
                <Route path="support/help" element={<SupportHelp />} />
                <Route path="support/tickets" element={<SupportTickets />} />
                <Route path="support/tickets/new" element={<SupportTicketNew />} />
                <Route path="support/tickets/:ticketId" element={<SupportTicketDetail />} />
                <Route path="congressional-activity" element={<ProtectedRoute requiredTier="alpha_operator"><CongressionalActivity /></ProtectedRoute>} />
                <Route path="admin" element={<AdminRoute><Administration /></AdminRoute>} />
              </Route>
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientContext.Provider>
  </QueryClientProvider>
);

export default App;
